import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./PasswordModal.css";

const PasswordModal = ({
  newPassword,
  setNewPassword,
  onSave,
  onCancel,
  isSaving = false,
}) => {
  const inputRef = useRef(null);

  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);

  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    inputRef.current?.focus();

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isSaving) {
        onCancel?.();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSaving, onCancel]);

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget && !isSaving) {
      onCancel?.();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const passwordValue = String(newPassword || "");

    if (!passwordValue.trim()) {
      setValidationError("Enter a new password.");
      inputRef.current?.focus();
      return;
    }

    if (passwordValue.length < 4) {
      setValidationError("Password must contain at least 4 characters.");
      inputRef.current?.focus();
      return;
    }

    if (passwordValue.length > 128) {
      setValidationError("Password cannot exceed 128 characters.");
      inputRef.current?.focus();
      return;
    }

    if (passwordValue !== confirmPassword) {
      setValidationError("Passwords do not match.");
      return;
    }

    setValidationError("");

    await onSave?.();
  };

  const handlePasswordInput = (event) => {
    setNewPassword(event.target.value);

    if (validationError) {
      setValidationError("");
    }
  };

  const handleConfirmInput = (event) => {
    setConfirmPassword(event.target.value);

    if (validationError) {
      setValidationError("");
    }
  };

  const handleCancel = () => {
    if (isSaving) return;

    setConfirmPassword("");
    setValidationError("");
    onCancel?.();
  };

  return createPortal(
    <div
      className="modal-backdrop"
      onMouseDown={handleBackdropClick}
      role="presentation"
    >
      <form
        className="modal-box password-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="password-modal-title"
        onSubmit={handleSubmit}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="password-modal-header">
          <div>
            <h3 id="password-modal-title" className="modal-title">
              Change Password
            </h3>

            <p>Enter and confirm the new password.</p>
          </div>

          <button
            type="button"
            className="password-modal-close"
            onClick={handleCancel}
            disabled={isSaving}
            aria-label="Close password modal"
          >
            ×
          </button>
        </div>

        <div className="password-modal-body">
          <div className="form-group">
            <label className="form-label" htmlFor="new-password">
              New Password
            </label>

            <div className="password-input-wrapper">
              <input
                id="new-password"
                ref={inputRef}
                type={showPassword ? "text" : "password"}
                className="form-input"
                placeholder="Enter new password"
                value={newPassword}
                onChange={handlePasswordInput}
                autoComplete="new-password"
                minLength={4}
                maxLength={128}
                disabled={isSaving}
              />

              <button
                type="button"
                className="password-visibility-button"
                onClick={() => setShowPassword((current) => !current)}
                disabled={isSaving}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="confirm-new-password">
              Confirm Password
            </label>

            <input
              id="confirm-new-password"
              type={showPassword ? "text" : "password"}
              className="form-input"
              placeholder="Re-enter new password"
              value={confirmPassword}
              onChange={handleConfirmInput}
              autoComplete="new-password"
              minLength={4}
              maxLength={128}
              disabled={isSaving}
            />
          </div>

          {validationError && (
            <div className="password-modal-error" role="alert">
              {validationError}
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button
            type="button"
            className="btn"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={
              isSaving || !String(newPassword || "").trim() || !confirmPassword
            }
          >
            {isSaving ? "Updating..." : "Update Password"}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
};

export default PasswordModal;
